import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { CLOSE_POPOVER } from '@frontendlib/components/features/popover/types/close-popover.injection-token';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { KpCountBadgeComponent } from '@modules/knowledge/components/kp-count-badge/kp-count-badge.component';
import { KPListType } from '@modules/knowledge/types/kp-list-type';
import { ViewSubjects } from '@sharedlib/common/permissions/types/Permissions';
import {
  KP_RESULT_TYPES,
  type KPResultCounts,
  type KPResultType,
  type KPResultTypeGroup,
} from '@modules/knowledge/types/kp-result-types';

interface TypeMenuRow extends KPResultType {
  readonly count: number;
  readonly active: boolean;
}

interface TypeMenuGroup {
  readonly group: KPResultTypeGroup;
  readonly rows: TypeMenuRow[];
}

@Component({
  selector: 'app-kp-stitch-type-context-menu',
  templateUrl: './stitch-type-context-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, KpCountBadgeComponent],
  host: {
    class: 'flex w-100 flex-col gap-2 px-6 py-8',
    role: 'menu',
  },
})
export class StitchTypeContextMenuComponent {
  private readonly permissionsService = inject(PermissionsService);
  public readonly value = input<KPListType>(KPListType.message);
  public readonly counts = input<KPResultCounts>({});

  public readonly typeChange = output<KPListType>();

  /** The catalogue's rows, split where the catalogue moves from one group to the next. */
  protected readonly groups = computed<TypeMenuGroup[]>(() => {
    const value = this.value();
    const counts = this.counts();
    const groups: TypeMenuGroup[] = [];

    for (const entry of KP_RESULT_TYPES) {
      if (
        (entry.type === KPListType.sheet || entry.type === KPListType.sheetFolder) &&
        !this.permissionsService.checkPermission('read', ViewSubjects.sheetsView)
      )
        continue;
      if (
        (entry.type === KPListType.form || entry.type === KPListType.formFolder) &&
        !this.permissionsService.checkPermission('read', ViewSubjects.formsView)
      ) continue;
      const row: TypeMenuRow = { ...entry, count: counts[entry.type] ?? 0, active: entry.type === value };
      const last = groups.at(-1);
      if (last?.group === entry.group) {
        last.rows.push(row);
      } else {
        groups.push({ group: entry.group, rows: [row] });
      }
    }

    return groups;
  });

  private readonly closePopover = inject(CLOSE_POPOVER, { optional: true });

  protected select(type: KPListType): void {
    this.typeChange.emit(type);
    this.closePopover?.next();
  }
}
